import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Welcome from "@/pages/Welcome";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Home from "@/pages/Home";
import Intake from "@/pages/Intake";
import PathPick from "@/pages/PathPick";
import Scan from "@/pages/Scan";
import Measure from "@/pages/Measure";
import Ask from "@/pages/Ask";
import Session from "@/pages/Session";
import Saved from "@/pages/Saved";
import Settings from "@/pages/Settings";
import Paywall from "@/pages/Paywall";
import Invoices from "@/pages/Invoices";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import AuthCallback from "@/pages/AuthCallback";
import Privacy from "@/pages/Privacy";
import Admin from "@/pages/Admin";
import Trends from "@/pages/Trends";
import Listings from "@/pages/Listings";
import ListingDetail from "@/pages/ListingDetail";
import Build from "@/pages/Build";
import Jobs from "@/pages/Jobs";
import { Layout } from "@/components/Layout";
import { UpgradeCard } from "@/components/Upgrade";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center label-mono">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function Paid({ children, feature }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center label-mono">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (!user.paid) return <Layout><UpgradeCard feature={feature} /></Layout>;
  return children;
}

function PaywallRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center label-mono">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.paid) return <Navigate to="/home" replace />;
  return <Paywall />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center label-mono">Loading…</div>;
  if (user) return <Navigate to="/home" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center label-mono">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

function TitleKeeper() {
  const loc = useLocation();
  useEffect(() => { document.title = "Path Mentor AI"; }, [loc.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TitleKeeper />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><Welcome /></PublicOnly>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/paywall" element={<PaywallRoute />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/home" element={<Protected><Home /></Protected>} />
            <Route path="/intake" element={<Paid feature="The guided mentor path"><Intake /></Paid>} />
            <Route path="/paths" element={<Paid feature="Path suggestions"><PathPick /></Paid>} />
            <Route path="/scan" element={<Protected><Scan /></Protected>} />
            <Route path="/trends" element={<Protected><Trends /></Protected>} />
            <Route path="/measure" element={<Protected><Measure /></Protected>} />
            <Route path="/build" element={<Paid feature="Design & Build It"><Build /></Paid>} />
            <Route path="/ask" element={<Paid feature="Talk it through with the mentor"><Ask /></Paid>} />
            <Route path="/session/:id" element={<Paid feature="Your saved path"><Session /></Paid>} />
            <Route path="/saved" element={<Paid feature="Saved items"><Saved /></Paid>} />
            <Route path="/invoices" element={<Paid feature="Invoices"><Invoices /></Paid>} />
            <Route path="/listings" element={<Paid feature="Products & Services"><Listings /></Paid>} />
            <Route path="/listings/:id" element={<Paid feature="Products & Services"><ListingDetail /></Paid>} />
            <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
